import cn from "classnames";
import _ from "lodash";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Media, Nav, NavbarBrand } from "reactstrap";
import logo from "../../../assets/wembii-logo.png";
import { COLOR_PRIMARY } from "../../../constants/theme";
import { useIntl } from "react-intl";
import { useFacebookCore } from "../../../cores/facebook";
import { useFacebookAdsCore } from "../../../cores/facebookAds";
import { useGoogleCore } from "../../../cores/google";
import { useInstagramCore } from "../../../cores/instagram";
import { useInstagramMenssegerCore } from "../../../cores/instagramMensseger";
import { useJumpsellerCore } from "../../../cores/jumpseller";
import { useLinkedInCore } from "../../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../../cores/linkedInBusiness";
import { useMeCore } from "../../../cores/me";
import { useMercadolibreCore } from "../../../cores/mercadolibre";
import { useRouterCore } from "../../../cores/router";
import { useTwitterCore } from "../../../cores/twitter";
import { useWooCommerceCore } from "../../../cores/wooCommerce";
import { PanelWembii } from "../PanelWembii";
import Protector from "../Protector";
import { JumpsellerIcon } from "../social/Jumpseller/JumpsellerIcon";
import { MercadolibreIcon } from "../social/Mercadolibre/MercadolibreIcon";
import { WooCommerceIcon } from "../social/WooCommerce/WooCommerceIcon";
import { TitleWembii } from "../TitleWembii";
import { NavItem2 } from "./NavItem2";
import { NavItemGroup } from "./NavItemGroup";
import { useGlobalCore } from "../../../cores/globals";

export default function SideBar() {
  const {
    routes,
    goToUsers,
    goToCompany,
    goToMeCompany,
    goToSocial,
    goToSocialPost,
    goToSocialHistory,
    goToInstagramAnalytics,
    goToFacebookAnalytics,
    goToWooCommerceAnalytics,
    goToGoogleAnalytics,
    goToWebForms,
    goToFacebookForms,
    goToEditWebChat,
    goToWebChat,
    goToWooCommerceOrders,
    goToEditWhatsappChat,
    goToMercadolibreOrders,
    goToFacebookCampaigns,
    goToMercadolibreAnalytics,
    goToContacts,
    goToContactCompanies,
    goToCalendar,
    goToSocialCalendar,
    goToReports,
    goToSocialStory,
    goToResellers,
    goToActivityBoards,
    goToBusinessBoard,
    goToBusinessBoardAnalytics,
    goToSocialManager,
    goToJumpsellerOrders,
    goToTasks,
    goToAutomations,
  } = useRouterCore();

  const intl = useIntl();
  const { company, user, companyPlan, companyRole, isAdmin, isReseller } = useMeCore();
  const { pathname } = useLocation();
  const { collapseSideBar, setCollapseSideBar } = useGlobalCore();
  const { settings: facebookSettings } = useFacebookCore();
  const { settings: facebookAdsSettings } = useFacebookAdsCore();
  const { settings: googleSettings } = useGoogleCore();
  const { settings: instagramSettings } = useInstagramCore();
  const { settings: instagramMenssegerSettings } = useInstagramMenssegerCore();
  const { settings: wooSettings } = useWooCommerceCore();
  const { settings: mercadolibreSettings } = useMercadolibreCore();
  const { settings: jumpsellerSettings } = useJumpsellerCore();
  const { settings: twitterSettings } = useTwitterCore();
  const { settings: linkedInSetting } = useLinkedInCore();
  const { settings: linkedInBusinessSetting } = useLinkedInBusinessCore();
  const {
    name: planName,
    rules: {
      allowAnalytics = false,
      allowFacebookAdvertisements = false,
      allowMarket = false,
      allowWebChat = false,
      allowWhatsappChat = false,
      allowActivityBoard = false,
      allowBusinessBoard = false,
      allowAutomation = false,
      allowDatabase = false,
      reportsAmount = 0,
      webFormsAmount = 0,
    } = {},
  } = companyPlan! || { name: "." };

  const isFacebookSync = useMemo(() => !_.isEmpty(facebookSettings), [
    facebookSettings,
  ]);
  const isLinkedInSync = useMemo(() => !_.isEmpty(linkedInSetting), [
    linkedInSetting,
  ]);
  const isLinkedInSBusinessSync = useMemo(
    () => !_.isEmpty(linkedInBusinessSetting),
    [linkedInBusinessSetting]
  );
  const isTwitterSync = useMemo(() => !_.isEmpty(twitterSettings), [
    twitterSettings,
  ]);
  const isFacebookAdsSync = useMemo(() => !_.isEmpty(facebookAdsSettings), [
    facebookAdsSettings,
  ]);
  const isInstagramSync = useMemo(() => !_.isEmpty(instagramSettings), [
    instagramSettings,
  ]);
  const isInstagramMenssegerSync = useMemo(() => !_.isEmpty(instagramMenssegerSettings), [
    instagramMenssegerSettings,
  ]);
  const isGoogleSync = useMemo(() => !_.isEmpty(googleSettings), [
    googleSettings,
  ]);
  const isWooSync = useMemo(() => !_.isEmpty(wooSettings), [wooSettings]);
  const isMercadolibreSync = useMemo(() => !_.isEmpty(mercadolibreSettings), [
    mercadolibreSettings,
  ]);
  const isJumpsellerSync = useMemo(() => !_.isEmpty(jumpsellerSettings), [
    jumpsellerSettings,
  ]);
  const isReportAllow = useMemo(() => reportsAmount > 0, [
    reportsAmount,
  ]);
  const isWebFormAllow = useMemo(() => webFormsAmount > 0, [
    webFormsAmount,
  ]);

  const socialPostActive = useMemo(
    () =>
      company != null &&
      (isFacebookSync ||
        isInstagramSync ||
        isTwitterSync ||
        isLinkedInSync ||
        isLinkedInSBusinessSync),
    [
      company,
      isFacebookSync,
      isInstagramSync,
      isLinkedInSBusinessSync,
      isLinkedInSync,
      isTwitterSync,
    ]
  );

  const allowChat = useMemo(
    () =>
      allowWebChat ||
      allowWhatsappChat ||
      isInstagramMenssegerSync ||
      isFacebookSync,
    [
      allowWebChat,
      isFacebookSync,
      isInstagramMenssegerSync,
      allowWhatsappChat,
    ]
  );

  const metricsActive = useMemo(
    () =>
      company != null &&
      (isFacebookSync ||
        isInstagramSync ||
        isGoogleSync ||
        isMercadolibreSync ||
        isWooSync),
    [
      company,
      isFacebookSync,
      isGoogleSync,
      isInstagramSync,
      isMercadolibreSync,
      isWooSync,
    ]
  );

  const planNotAllowedMessage = useMemo(
    () => `Plan ${planName.split(".")[2]} no permite esta funcionalidad`,
    [planName]
  );

  /*const onCanvaClick = useCallback(() => {*/
  /*  openCanva((result) => {*/
  /*    window.open(result.exportUrl);*/
  /*  });*/
  /*}, [openCanva]);*/

  return (
    <PanelWembii
      className={cn("h-100", {
        sidebarExpanded: !collapseSideBar,
        sidebarCollaped: collapseSideBar,
      })}
      onMouseEnter={() => setCollapseSideBar(false)}
      onMouseLeave={() => setCollapseSideBar(true)}
    >
      <div className={cn("p-3", {
        "overflow-auto": !collapseSideBar,
        "overflow-hidden": collapseSideBar,
      })}>
        <Nav vertical>
          <NavbarBrand href="/social" className="m-0">
            <Media
              className="w-100"
              object
              src={logo}
              alt="Generic placeholder image"
            />
          </NavbarBrand>
          <hr className="horizontal-separator my-2" />
          <Protector active={company != null}>
            <NavItem2
              onClick={goToSocial}
              label={intl.formatMessage({ id: "app.sidebar.home" })}
              iconName="home"
              activeUrl={routes.social}
            />
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.social === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.socialMedia" })}
              iconName="thumbs-up"
              disabled={!socialPostActive}
              activeUrls={[
                routes.socialPost,
                routes.socialStory,
                routes.socialHistory,
                routes.socialCalendar,
              ]}
            >
              <NavItem2
                activeUrl={routes.socialPost}
                onClick={() => goToSocialPost("")}
                label={intl.formatMessage({ id: "app.sidebar.socialMediaPost" })}
                iconName="thumbs-up"
              />
              <NavItem2
                activeUrl={routes.socialStory}
                onClick={() => goToSocialStory("")}
                label={intl.formatMessage({ id: "app.sidebar.socialStory" })}
                iconName="thumbs-up"
              />
              <NavItem2
                activeUrl={routes.socialCalendar}
                onClick={goToSocialCalendar}
                label={intl.formatMessage({ id: "app.sidebar.socialMediaCalendar" })}
                iconName="calendar"
              />
              <NavItem2
                activeUrl={routes.socialHistory}
                onClick={goToSocialHistory}
                label={intl.formatMessage({ id: "app.sidebar.socialMediaRecord" })}
                iconName="history"
              />
            </NavItemGroup>
          </Protector>
          {/*<Protector active={company != null}>*/}
          {/*  <NavItemGroup */}
          {/*    label={intl.formatMessage({ id: "app.sidebar.design" })} */}
          {/*    iconName="edit" activeUrls={[]}>*/}
          {/*    <NavItem2*/}
          {/*      onClick={onCanvaClick}*/}
          {/*      label={intl.formatMessage({ id: "app.sidebar.designCanva" })} */}
          {/*      iconName="edit"*/}
          {/*      disabled={!allowCanva}*/}
          {/*      disabledMessage={planNotAllowedMessage}*/}
          {/*    />*/}
          {/*  </NavItemGroup>*/}
          {/*</Protector>*/}
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.ads === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.advertisements" })}
              iconName="bullhorn"
              activeUrls={[routes.facebookCampaigns, routes.createCampaign]}
              disabled={!allowFacebookAdvertisements || !isFacebookAdsSync}
              disabledMessage={planNotAllowedMessage}
            >
              <NavItem2
                activeUrl={routes.facebookCampaigns}
                onClick={goToFacebookCampaigns}
                label={intl.formatMessage({ id: "app.sidebar.advertisements.FaceAndIns" })}
                iconName="ad"
                disabled={!isFacebookAdsSync}
              />
            </NavItemGroup>
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.forms === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.forms" })}
              iconName="wpforms"
              iconPrefix="fab"
              activeUrls={[routes.webForms, routes.facebookForm]}
            >
              <NavItem2
                activeUrl={routes.webForms}
                onClick={goToWebForms}
                label={intl.formatMessage({ id: "app.sidebar.forms.Web" })}
                iconName="wpforms"
                iconPrefix="fab"
                disabled={!isWebFormAllow}
              />
              <NavItem2
                activeUrl={routes.facebookForm}
                onClick={goToFacebookForms}
                label={intl.formatMessage({ id: "app.sidebar.forms.Facebook" })}
                iconName="facebook"
                iconPrefix="fab"
                disabled={!isFacebookSync}
              />
            </NavItemGroup>
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.chat === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.chat" })}
              iconName="comments"
              activeUrls={[
                routes.webChat,
                routes.editWebChat,
                routes.editWhatsappChat,
              ]}
              disabled={!allowChat}
              disabledMessage={planNotAllowedMessage}
            >
              <NavItem2
                activeUrl={routes.webChat}
                onClick={goToWebChat}
                label={intl.formatMessage({ id: "app.sidebar.chat.conversations" })}
                iconName="comments"
                disabled={!allowWebChat}
                disabledMessage={planNotAllowedMessage}
              />
              <NavItem2
                activeUrl={routes.editWebChat}
                onClick={goToEditWebChat}
                label={intl.formatMessage({ id: "app.sidebar.chat.WebChatConfig" })}
                iconName="cog"
                disabled={!allowWebChat}
                disabledMessage={planNotAllowedMessage}
              />
              <NavItem2
                activeUrl={routes.editWhatsappChat}
                onClick={goToEditWhatsappChat}
                label={intl.formatMessage({ id: "app.sidebar.chat.whatsappConfig" })}
                iconPrefix="fab"
                iconName="whatsapp"
                disabled={!allowWhatsappChat}
                disabledMessage={planNotAllowedMessage}
              />
            </NavItemGroup>
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.activities === true
            )
          }>
            <NavItem2
              onClick={goToActivityBoards}
              label={intl.formatMessage({ id: "app.sidebar.activities" })}
              iconName="columns"
              activeUrl={routes.activityBoards}
              disabled={!allowActivityBoard}
              disabledMessage={planNotAllowedMessage}
            />
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.market === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.OnlineSales" })}
              iconName="store"
              activeUrls={[routes.wooCommerceOrders, routes.mercadolibreOrders]}
              disabled={!allowMarket}
              disabledMessage={planNotAllowedMessage}
            >
              <NavItem2
                activeUrl={routes.wooCommerceOrders}
                onClick={goToWooCommerceOrders}
                label={intl.formatMessage({ id: "app.sidebar.OnlineSales.WooCommerce" })}
                icon={
                  <WooCommerceIcon
                    color={
                      pathname === routes.wooCommerceOrders
                        ? COLOR_PRIMARY
                        : "grey"
                    }
                    className={cn("mr-3", { "text-black-50": !isWooSync })}
                    size="1x"
                  />
                }
                disabled={!isWooSync}
              />
              <NavItem2
                activeUrl={routes.mercadolibreOrders}
                onClick={goToMercadolibreOrders}
                label={intl.formatMessage({ id: "app.sidebar.OnlineSales.Mercadolibre" })}
                icon={
                  <MercadolibreIcon
                    color={
                      pathname === routes.mercadolibreOrders
                        ? COLOR_PRIMARY
                        : "grey"
                    }
                    className={cn("mr-3", {
                      "text-black-50": !isMercadolibreSync,
                    })}
                    size="1x"
                  />
                }
                disabled={!isMercadolibreSync}
              />
              <NavItem2
                activeUrl={routes.jumpsellerOrders}
                onClick={goToJumpsellerOrders}
                label={intl.formatMessage({
                  id: "app.sidebar.OnlineSales.Jumpseller",
                })}
                icon={
                  <JumpsellerIcon
                    color={
                      pathname === routes.jumpsellerOrders
                        ? COLOR_PRIMARY
                        : "grey"
                    }
                    className={cn("mr-3", {
                      "text-black-50": !isJumpsellerSync,
                    })}
                    size="1x"
                  />
                }
                disabled={!isJumpsellerSync}
              />
            </NavItemGroup>
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.crm === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.crm" })}
              iconName="funnel-dollar"
              disabled={!allowBusinessBoard}
              disabledMessage={planNotAllowedMessage}
              activeUrls={[
                routes.businessBoard,
                routes.tasks,
                routes.businessBoardAnalytics,
                routes.calendar
              ]}
            >
              <NavItem2
                onClick={goToTasks}
                label={intl.formatMessage({ id: "app.sidebar.tasks" })}
                iconName="tasks"
                activeUrl={routes.tasks}
              />
              <NavItem2
                activeUrl={routes.calendar}
                onClick={goToCalendar}
                label={intl.formatMessage({ id: "app.sidebar.meetings" })}
                iconName="calendar"
              />
              <NavItem2
                onClick={goToBusinessBoard}
                label={intl.formatMessage({ id: "app.sidebar.crm.business" })}
                iconName="briefcase"
                activeUrl={routes.businessBoard}
              />
              <NavItem2
                onClick={goToBusinessBoardAnalytics}
                label={intl.formatMessage({ id: "app.sidebar.crm.metrics" })}
                iconName="chart-bar"
                activeUrl={routes.businessBoardAnalytics}
              />
            </NavItemGroup>
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.contacts === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.database" })}
              iconName="database"
              activeUrls={[
                routes.contacts,
                routes.contactCompanies,
              ]}
              disabled={!allowDatabase}
              disabledMessage={planNotAllowedMessage}
            >
              <NavItem2
                activeUrl={routes.contacts}
                onClick={goToContacts}
                label={intl.formatMessage({ id: "app.sidebar.contacts" })}
                iconName="address-book"
              />
              <NavItem2
                activeUrl={routes.contactCompanies}
                onClick={goToContactCompanies}
                label={intl.formatMessage({ id: "app.sidebar.contact.companies" })}
                iconName="building"
              />
            </NavItemGroup>
          </Protector>
          <Protector active={
            metricsActive && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.analitycs === true
            )
          }>
            <NavItemGroup
              label={intl.formatMessage({ id: "app.sidebar.metrics" })}
              iconName="chart-area"
              activeUrls={[
                routes.googleAnalytics,
                routes.facebookAnalytics,
                routes.instagramAnalytics,
                routes.wooCommerceAnalytics,
                routes.mercadolibreAnalytics,
              ]}
              disabled={!allowAnalytics}
              disabledMessage={planNotAllowedMessage}
            >
              <NavItem2
                activeUrl={routes.facebookAnalytics}
                onClick={goToFacebookAnalytics}
                label={intl.formatMessage({ id: "app.sidebar.metrics.Facebook" })}
                iconName="facebook"
                iconPrefix="fab"
                disabled={!isFacebookSync}
              />
              <NavItem2
                activeUrl={routes.instagramAnalytics}
                onClick={goToInstagramAnalytics}
                label={intl.formatMessage({ id: "app.sidebar.metrics.Instagram" })}
                iconName="instagram"
                iconPrefix="fab"
                disabled={!isInstagramSync}
              />
              <NavItem2
                activeUrl={routes.googleAnalytics}
                onClick={goToGoogleAnalytics}
                label={intl.formatMessage({ id: "app.sidebar.metrics.GoogleAnalytics" })}
                iconName="google"
                iconPrefix="fab"
                disabled={!isGoogleSync}
              />
              <NavItem2
                activeUrl={routes.wooCommerceAnalytics}
                onClick={goToWooCommerceAnalytics}
                label={intl.formatMessage({ id: "app.sidebar.metrics.WooCommerce" })}
                icon={
                  <WooCommerceIcon
                    color={
                      pathname === routes.wooCommerceAnalytics
                        ? COLOR_PRIMARY
                        : "grey"
                    }
                    className={cn("mr-3", { "text-black-50": !isWooSync })}
                    size="1x"
                  />
                }
                disabled={!isWooSync}
              />
              <NavItem2
                activeUrl={routes.mercadolibreAnalytics}
                onClick={goToMercadolibreAnalytics}
                label={intl.formatMessage({ id: "app.sidebar.metrics.MercLibre" })}
                icon={
                  <MercadolibreIcon
                    color={
                      pathname === routes.mercadolibreAnalytics
                        ? COLOR_PRIMARY
                        : "grey"
                    }
                    className={cn("mr-3", {
                      "text-black-50": !isMercadolibreSync,
                    })}
                    size="1x"
                  />
                }
                disabled={!isMercadolibreSync}
              />
            </NavItemGroup>
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.reports === true
            )
          }>
            <NavItem2
              onClick={goToReports}
              label={intl.formatMessage({ id: "app.sidebar.reports" })}
              iconName="file"
              activeUrl={routes.reports}
              disabled={!isReportAllow}
            />
          </Protector>
          <Protector active={
            company != null && (
              isReseller ||
              isAdmin ||
              companyRole?.owner === true ||
              companyRole?.admin === true ||
              companyRole?.agent === true ||
              companyRole?.automations === true
            )
          }>
            <NavItem2
              onClick={goToAutomations}
              label={intl.formatMessage({ id: "app.sidebar.automations" })}
              iconName="robot"
              disabled={!allowAutomation}
              activeUrl={routes.automations}
            />
          </Protector>
        </Nav>

        {!isAdmin && (
          <>
            <hr className="horizontal-separator my-3" />
            {!collapseSideBar &&
              <TitleWembii Tag="h6">{intl.formatMessage({ id: "app.sidebar.myPlan" })}</TitleWembii>
            }
            <Nav vertical>
              <NavItem2
                onClick={goToMeCompany}
                label={intl.formatMessage({ id: "app.sidebar.company" })}
                iconName="building"
                activeUrl={routes.meCompany}
              />
            </Nav>
          </>
        )}

        {isReseller && user?.reseller && (
          <>
            <hr className="horizontal-separator my-3" />
            {!collapseSideBar &&
              <TitleWembii Tag="h6">
                {user.reseller.name} {intl.formatMessage({ id: "app.sidebar.isReseller" })}
              </TitleWembii>
            }
            <Nav vertical>
              <NavItem2
                onClick={goToUsers}
                label={intl.formatMessage({ id: "app.sidebar.users" })}
                iconName="users"
                activeUrl={routes.users}
              />
              <NavItem2
                onClick={goToCompany}
                label={intl.formatMessage({ id: "app.sidebar.company" })}
                iconName="building"
                activeUrl={routes.company}
              />
            </Nav>
          </>
        )}
        {isAdmin && (
          <>
            <hr className="horizontal-separator my-3" />
            {!collapseSideBar &&
              <TitleWembii Tag="h6">
                {intl.formatMessage({ id: "app.sidebar.adminWembii" })}
              </TitleWembii>
            }
            <Nav vertical>
              <NavItem2
                onClick={goToUsers}
                label={intl.formatMessage({ id: "app.sidebar.users" })}
                iconName="users"
                activeUrl={routes.users}
              />
              <NavItem2
                onClick={goToCompany}
                label={intl.formatMessage({ id: "app.sidebar.company" })}
                iconName="building"
                activeUrl={routes.company}
              />
              <NavItem2
                onClick={goToResellers}
                label={intl.formatMessage({ id: "app.sidebar.adminWembii.reseller" })}
                iconName="comment-dollar"
                activeUrl={routes.resellers}
              />
              <NavItem2
                onClick={goToSocialManager}
                label={intl.formatMessage({ id: "app.sidebar.adminWembii.socialManager" })}
                iconName="thumbs-up"
                activeUrl={routes.socialManager}
              />
            </Nav>
          </>
        )}
      </div>
    </PanelWembii>
  );
}
